/**
 * Admin → Discover — overview landing page (AUTO-8).
 *
 * Six cards (tools / skills / prompts / functions / agents / connections) +
 * a "recently added" rail. Each card links to the per-kind browse list at
 * /admin/discover/<kind>.
 *
 * Studio is the AUTHORING surface (what the tenant already has). Discover
 * is the BROWSE surface (what's available in the system catalog). They
 * are intentionally separate.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Wrench,
  Sparkles,
  MessageSquare,
  Code2,
  Bot,
  Plug,
  ArrowUpRight,
  Compass,
  Clock,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { apiClient } from '@/lib/api-client';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/discover/')({
  component: DiscoverIndexPage,
});

interface OverviewData {
  counts: Record<
    'tool' | 'skill' | 'prompt' | 'function' | 'agent' | 'connection',
    number
  >;
  recentlyAdded: Array<{
    kind: 'tool' | 'skill' | 'prompt' | 'function' | 'agent' | 'connection';
    slug: string;
    displayName: string;
    addedAt: string;
  }>;
}

const KIND_META = {
  tool: {
    label: 'Tools',
    icon: Wrench,
    description:
      'Function-callable capabilities the LLM can invoke — read knowledge, calculate, summarize, send messages, query external systems.',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    path: '/admin/discover/tools',
  },
  skill: {
    label: 'Skills',
    icon: Sparkles,
    description:
      'Reusable instruction bundles that compose tools + prompts into a named capability (e.g. maintenance analysis, parts lookup).',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    path: '/admin/discover/skills',
  },
  prompt: {
    label: 'Prompts',
    icon: MessageSquare,
    description:
      'Reusable LLM prompt templates with named variables. Preview against argus_fast before wiring into an agent.',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    path: '/admin/discover/prompts',
  },
  function: {
    label: 'Functions',
    icon: Code2,
    description:
      'Argus wrapper functions — sandboxed code that runs on the platform (HTTP, SQL, Lua, etc.) and is callable from agents.',
    cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
    path: '/admin/discover/functions',
  },
  agent: {
    label: 'Agents',
    icon: Bot,
    description:
      'Pre-built agent templates you can clone into your tenant. Composes a model brand + system prompt + tools + skills.',
    cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
    path: '/admin/discover/agents',
  },
  connection: {
    label: 'Connections',
    icon: Plug,
    description:
      'MCP server connectors — both system-shipped and tenant-installed external systems (SAP, ServiceNow, etc.).',
    cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    path: '/admin/discover/connections',
  },
} as const;

function DiscoverIndexPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'discover', 'overview'],
    queryFn: () => apiClient.get<OverviewData>('/admin/discover/__overview'),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="space-y-1">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Compass className="h-4 w-4 text-muted-foreground" />
          Discover catalog
        </h2>
        <p className="text-xs text-muted-foreground max-w-2xl">
          Browse every entity available to this tenant: tools the LLM can call,
          skill bundles, prompt templates, agent templates, and external
          connectors. Tenant-private and system-shipped rows are merged into
          one ranked view.
        </p>
      </div>

      {/* Card grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="py-8 text-center text-muted-foreground animate-pulse">
                Loading...
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive">
            Failed to load overview: {error instanceof Error ? error.message : String(error)}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(
            Object.entries(KIND_META) as Array<
              [keyof typeof KIND_META, (typeof KIND_META)[keyof typeof KIND_META]]
            >
          ).map(([kind, meta]) => {
            const Icon = meta.icon;
            const count = data?.counts[kind] ?? 0;
            return (
              <Link
                key={kind}
                to={meta.path}
                className="group"
                title={`Browse ${meta.label.toLowerCase()}`}
              >
                <Card className="h-full transition-all group-hover:border-foreground/30 group-hover:shadow-sm">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className={cn(
                          'h-10 w-10 rounded-md border flex items-center justify-center shrink-0',
                          meta.cls,
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold tabular-nums">
                        {count}
                      </span>
                      <span className="text-sm font-medium">{meta.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {meta.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Recently added */}
      {data && data.recentlyAdded.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              Recently added
            </h3>
            <ul className="divide-y divide-border">
              {data.recentlyAdded.map((row) => {
                const meta = KIND_META[row.kind];
                const Icon = meta.icon;
                return (
                  <li key={`${row.kind}:${row.slug}`}>
                    <Link
                      to={`/admin/discover/${row.kind === 'tool' ? 'tools' : row.kind === 'skill' ? 'skills' : row.kind === 'prompt' ? 'prompts' : row.kind === 'function' ? 'functions' : row.kind === 'agent' ? 'agents' : 'connections'}`}
                      className="flex items-center gap-3 py-2 hover:bg-accent/50 -mx-2 px-2 rounded"
                    >
                      <div
                        className={cn(
                          'h-6 w-6 rounded border flex items-center justify-center shrink-0',
                          meta.cls,
                        )}
                      >
                        <Icon className="h-3 w-3" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">
                          {row.displayName}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground truncate">
                          {row.slug}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {row.kind}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                        {relativeTime(row.addedAt)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
